import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, X, Check, Plus } from "lucide-react";

/**
 * SearchableSelect — a typed-search combobox replacement for native <select>.
 *
 * Why: native <select> only matches the FIRST character of the option label
 * for keyboard search. For a customer with 400+ records, reps need to find by
 * VAT number, account/external_ref, phone, email, or any sub-string of name —
 * which a native select can't do.
 *
 * Props:
 *   - value: string (selected option id) | ""
 *   - onChange: (id) => void
 *   - options: [{ id, label, sublabel?, searchTerms?: string[] }]
 *       searchTerms — extra haystack tokens to match against (vat, phone,
 *       account number, email, etc.). Concatenated with label + sublabel.
 *   - placeholder?: string
 *   - disabled?: boolean
 *   - required?: boolean
 *   - testId?: string
 *   - emptyHint?: string  — shown when 0 options match the search term.
 *   - onCreateNew?: (query) => void  — when provided, an in-list "Create new"
 *       CTA renders whenever the search returns zero results. Closes the
 *       dropdown then calls the callback so the parent can navigate the user
 *       to the relevant full-page editor (carrying the query as a name hint).
 *   - createNewLabel?: string  — optional override label, defaults to
 *       "Create new \"<query>\"" / "Create new" when query is empty.
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "— pick one —",
  disabled = false,
  required = false,
  testId,
  emptyHint = "No matches.",
  onCreateNew,
  createNewLabel,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  const selected = useMemo(
    () => options.find((o) => o.id === value) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => {
      const haystack = [
        o.label,
        o.sublabel,
        ...(o.searchTerms || []),
      ]
        .filter(Boolean)
        .map((s) => String(s).toLowerCase())
        .join(" | ");
      // Multi-word search — every word must match somewhere in the haystack.
      return q.split(/\s+/).every((w) => haystack.includes(w));
    });
  }, [options, query]);

  // Close on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Focus search input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  const onPick = (id) => {
    onChange?.(id);
    setOpen(false);
    setQuery("");
  };

  const onClear = (e) => {
    e?.stopPropagation?.();
    onChange?.("");
    setQuery("");
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "Enter" && filtered.length === 1) {
      e.preventDefault();
      onPick(filtered[0].id);
    }
  };

  return (
    <div className="relative" ref={wrapperRef} data-testid={testId}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`hv-input mt-1 text-sm w-full text-left flex items-center justify-between gap-2 ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
        data-testid={testId ? `${testId}-trigger` : undefined}
      >
        <span className={`truncate ${selected ? "" : "text-[#9aa3b5]"}`}>
          {selected ? (
            <>
              {selected.label}
              {selected.sublabel ? <span className="text-[#9aa3b5] text-xs"> · {selected.sublabel}</span> : null}
            </>
          ) : placeholder}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          {selected && !disabled && !required && (
            <span
              role="button"
              tabIndex={0}
              onClick={onClear}
              onKeyDown={(e) => { if (e.key === "Enter") onClear(e); }}
              className="text-[#9aa3b5] hover:text-rose-300 inline-flex"
              data-testid={testId ? `${testId}-clear` : undefined}
              aria-label="Clear"
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-[#9aa3b5] transition ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-white/10 bg-[#121419] shadow-2xl overflow-hidden"
          data-testid={testId ? `${testId}-dropdown` : undefined}
        >
          <div className="relative border-b border-white/5">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9aa3b5]" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Search by name, account, VAT, phone, email…"
              className="w-full pl-8 pr-2 py-2 bg-transparent text-sm outline-none border-none"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>

          <div className="max-h-72 overflow-y-auto hv-scroll" role="listbox">
            {filtered.length === 0 ? (
              <div className="py-3 px-2 flex flex-col items-stretch gap-2">
                <div
                  className="px-1 text-center text-xs text-[#9aa3b5] italic"
                  data-testid={testId ? `${testId}-empty` : undefined}
                >
                  {emptyHint}
                </div>
                {onCreateNew && (
                  <button
                    type="button"
                    onClick={() => {
                      const q = query.trim();
                      setOpen(false);
                      setQuery("");
                      onCreateNew(q);
                    }}
                    className="hv-btn hv-btn-primary mx-2 text-xs justify-center py-2"
                    data-testid={testId ? `${testId}-create-new` : undefined}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {createNewLabel || (query.trim()
                      ? `Create new "${query.trim()}"`
                      : "Create new")}
                  </button>
                )}
              </div>
            ) : (
              filtered.map((o) => {
                const isSel = o.id === value;
                return (
                  <button
                    type="button"
                    key={o.id}
                    onClick={() => onPick(o.id)}
                    className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 transition ${isSel ? "bg-[#1FB6C1]/15 text-[#1FB6C1]" : "hover:bg-white/5"}`}
                    data-testid={testId ? `${testId}-option-${o.id}` : undefined}
                    role="option"
                    aria-selected={isSel}
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{o.label}</span>
                      {o.sublabel && (
                        <span className="block text-[10px] text-[#9aa3b5] truncate">{o.sublabel}</span>
                      )}
                    </span>
                    {isSel && <Check className="w-3.5 h-3.5 text-[#1FB6C1] shrink-0 mt-0.5" />}
                  </button>
                );
              })
            )}
          </div>

          <div className="px-3 py-1.5 border-t border-white/5 flex items-center justify-between text-[10px] text-[#9aa3b5]">
            <span data-testid={testId ? `${testId}-count` : undefined}>
              {filtered.length} of {options.length}
            </span>
            <span className="italic">Esc to close</span>
          </div>
        </div>
      )}
    </div>
  );
}
